import {
  Component, OnDestroy, OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CourseValidationDetailsComponent } from './components/course-validation-details/course-validation-details.component';
import { CatalogCourseDetailsComponent } from '../course-catalog/components/catalog-course-details/catalog-course-details.component';
import { ValidationService } from 'src/app/services/validation.service';
import { EntityService } from 'src/app/services/entity.service';
import { formatDate as formatDateUtil } from 'src/app/utils/dates.utils';

@Component({
  selector: 'app-validation',
  templateUrl: './validation.component.html',
  styleUrls: [ './validation.component.scss' ],
})
export class ValidationComponent implements OnInit, OnDestroy {
  selectedTab = 'Need Validation';
  isLoading = true;
  validationSubscription: any;
  sortDirection = 'ASC';
  stringFilter = '';
  error: any;
  isNeedsValidationTab = true;
  
  tableData: any;
  courseList: any;
  _validationData: any;
  offset = 0;
  selectedItemsPerPage = 25;
  sortColumnName = 'courseName';
  originalValidationResponse: any;

  constructor( 
    public dialog: MatDialog, 
    private validationService: ValidationService,
    private entityService: EntityService,
  ) { }

  ngOnInit(): void {
    this.getValidationSessions();
    this.isLoading = true;
    
    this.validationSubscription = this.validationService.validationData$.subscribe((response: any) => {
      this._validationData = response;
      this.originalValidationResponse = { ...response };
      this.error = null;
      this.courseList = response?.rows;
      
      // Format data for app-table component
      const tz = this.entityService.getLocalTimezone() ?? undefined;
      const formattedTableData = this.courseList?.map((course: any) => {
        course.formattedDate = formatDateUtil.asDateLocal(course.dateOffered) || '-';
        const startDate = formatDateUtil.parseSessionTimeAsUtc(course?.sessionStartDate);
        const endDate = formatDateUtil.parseSessionTimeAsUtc(course?.sessionEndDate);
        course.sessionStartTime = startDate ? formatDateUtil.asTimeWithTimezone(startDate, tz) : '';
        course.sessionEndTime = endDate ? formatDateUtil.asTimeWithTimezone(endDate, tz) : '';

        return {
          itemId: course.courseItemId,
          itemData: [
            {
              content: course?.courseName,
              class: 'kbody-medium left-align bold',
              secondLine: course?.courseItemId,
            },
            {
              content: course?.gcRegisteredCount,
              class: 'skinny left-align',
            },
            {
              content: course?.location,
              class: 'medium left-align',
            },
            {
              content: course?.formattedDate,
              class: 'medium left-align',
            },
            {
              content: `${course?.sessionStartTime?.toLowerCase()} - ${course?.sessionEndTime?.toLowerCase()}`,
              class: 'medium left-align',
            },
            {
              content: course?.courseItemId,
              class: 'right',
            },
          ],
        };
      });

      this.tableData = {
        tableTitle: this.selectedTab === 'Need Validation' 
          ? 'Courses that need validation' 
          : 'Previously validated courses',
        error: response?.error || null,
        columnTitles: [
          { title: 'Course Name', sortBy: 'courseName' },
          { 
            title: 'Members', 
            class: 'skinny left-align', 
            sortBy: 'gcRegisteredCount' 
          },
          { 
            title: 'Location', 
            class: 'medium left-align', 
            sortBy: 'location' 
          },
          { 
            title: 'Date', 
            class: 'medium left-align',
            sortBy: 'dateOffered' 
          },
          { 
            title: 'Time', 
            class: 'medium left-align',
            sortBy: 'sessionStartDate' 
          },
          { 
            title: 'Course ID', 
            class: 'right', 
            sortBy: 'courseItemId' 
          },
        ],
        totalItems: response?.pagination?.totalItems || this.courseList?.length || 0,
        curPage: response?.pagination?.curPage || 1,
        totalPages: response?.pagination?.totalPages || 1,
        data: formattedTableData,
      };

      this.isLoading = this.courseList ? false : true;
      
      if(response?.error) {
        this.error = response.error;
        this.isLoading = false;
      }
    });
  }

  getValidationSessions = () => {
    this.validationService.getValidationSessions(
      this.isNeedsValidationTab,
      {
        sortColumn: this.sortColumnName,
        sortDirection: this.sortDirection,
        filterString: this.stringFilter,
        limit: this.selectedItemsPerPage,
        offset: this.offset,
      }
    );
  };

  calculateOffset(page: number): number {
    return (page - 1) * this.selectedItemsPerPage;
  }

  onPageChanged(page: number) {
    const offset = this.calculateOffset(page);
    this.offset = offset;
    this.courseList = [];
    this.isLoading = true;
    this.getValidationSessions();
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.selectedItemsPerPage = itemsPerPage;
    this.offset = 0;
    this.courseList = [];
    this.isLoading = true;
    this.getValidationSessions();
  }

  sortColumn = (sortOrder: any) => {
    this.sortColumnName = sortOrder.sortBy;
    this.sortDirection = sortOrder.direction;
    
    const currentPage = this.originalValidationResponse?.pagination?.curPage || 1;
    const offset = this.calculateOffset(currentPage);
    this.offset = offset;
    
    this.courseList = [];
    this.isLoading = true;
    this.getValidationSessions();
  };

  search = (filterData: any) => {
    const { searchText } = filterData;
    this.stringFilter = searchText;
    this.offset = 0;
    this.courseList = [];
    this.isLoading = true;
    this.getValidationSessions();
  };

  setSelectedTab = (tab: any) => {
    this.courseList = null;
    this.isLoading = true;
    this.selectedTab = tab;
    this.offset = 0;
    this.isNeedsValidationTab = tab === 'Need Validation';
    this.getValidationSessions();
  };

  openCourseValidationDetails = (itemId: any) => {
    const course = this.courseList?.find((c: any) => c.courseItemId === itemId);
    if (!course) return;
    
    const courseDialogRef = this.dialog.open(CourseValidationDetailsComponent, { 
      data: course 
    });
    
    courseDialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.isLoading = true;
        this.courseList = [];
        this.getValidationSessions();
      }
    });
  };

  openCourseDetails = (course: any) => {
    const dialogRef = this.dialog.open(CatalogCourseDetailsComponent, { 
      data: { 
        selectedCourse: course, 
        noRegistrationButtons: true 
      } 
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.getValidationSessions();
      }
    });
  };

  ngOnDestroy(): void {
    this.validationSubscription.unsubscribe();
  }
}